import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import ScorecardBanner from "./scorecard/ScorecardBanner";
import { useEffect, useMemo, useState } from "react";
import {
  LayoutDashboard,
  Users,
  GitBranch,
  ClipboardList,
  LifeBuoy,
  CalendarClock,
  CalendarDays,
  CheckSquare,
  Phone,
  Settings,
  Home,
  LogOut,
  Boxes,
  ScanText,
  FileSignature,
  Wrench,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  ArrowLeft,
  ArrowRight,
  BarChart3,
  UserPlus,
  Inbox as InboxIcon,
  BellRing,
  ShoppingCart,
  MoreHorizontal,
  Map as MapIcon,
  MapPin,
  TrendingUp,
  Tags,
  Briefcase,
  Receipt as ReceiptIcon,
  Clock as ClockIcon,
} from "lucide-react";
import { MessageSquare, FlaskConical, Building2, Bug as BugIcon, Image as ImageIcon, Database, PackageCheck, Calculator, Globe, Plus, Menu as MenuIcon, Trash2 as Trash2Icon, Droplets } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/TenantBrandingContext";
import { useTheme } from "../context/ThemeContext";
import QuickActionsLauncher from "./QuickActionsLauncher";
// Iter 228 — QuickLogCall removed from UI; backend route + collection
// preserved. The "log a call" action wasn't used in HV's daily flow.
import { HidroLauncherButton } from "./HidroAssistant";
import NavHistoryButtons from "./NavHistoryButtons";
import OfflineIndicator from "./OfflineIndicator";
import NotificationsBell from "./NotificationsBell";
import { CoachCheckinModal } from "./CoachPill";
import GlobalReportBugButton from "./GlobalReportBugButton";
// Iter 290zp.13z21 — GlobalDocDropZone removed per owner directive.
// The fullscreen "Drop to auto-OCR" modal could lock the app when an
// upload hung (X only flipped `active`, not `busy`). Worse, it routed
// drops on a customer page to /ocr/invoices unlinked, so the dropped
// invoice never appeared on that customer. Per-surface dropzones
// (AccountBlock category cards, etc.) now handle drops contextually.
import UploadDock from "./UploadDock";
import UploadDockCrashFallback from "./UploadDockCrashFallback";
import SyncButton from "./SyncButton";
import CommandPalette from "./CommandPalette";
import BulletinBanner from "./BulletinBanner";
import PriorityAlertBanner from "./dashboard/PriorityAlertBanner";
import ClockReminderBanner from "./clock/ClockReminderBanner";
import EntityTrail from "./EntityTrail";
import DemoBanner from "./DemoBanner";
import useKeyboardOpen from "../lib/useKeyboardOpen";
import { useTicketNotifications } from "../lib/notifications";
import { tap } from "../lib/haptics";
import api from "../lib/api";
import Avatar from "./Avatar";
import ErrorBoundary from "./ErrorBoundary";
import MobileMoreDrawer from "./MobileMoreDrawer";

// Per-role visible nav. `client` is handled separately by PortalLayout.
// `owner` behaves identically to `admin` for nav purposes.
// Each NAV item has a `perm` — the permission key that controls its
// visibility. Items are filtered via `useAuth().hasPermission()` at runtime
// so role customisations + per-user overrides take effect instantly.
const TOOLS_CHILDREN = [
  { toolKey: "tool.equipment",              to: "/equipment", label: "Equipment & Inventory", icon: Wrench, perm: "nav.equipment" },
  { toolKey: "tool.picking",                to: "/picking", label: "Picking", icon: PackageCheck, perm: "nav.tools" },
  { toolKey: "tool.engineering_desk",       to: "/tools/engineering-desk", label: "Engineering Desk", icon: FlaskConical, perm: "nav.tools" },
  { toolKey: "tool.gallery",                to: "/gallery", label: "Gallery", icon: ImageIcon, perm: "nav.tools" },
  { toolKey: "tool.suppliers",              to: "/suppliers", label: "Suppliers", icon: Building2, perm: "nav.tools", roles: ["owner", "admin", "manager"] },
  { toolKey: "tool.onboarding",             to: "/tools/onboarding?new=prospect", label: "Lead Onboarding", icon: UserPlus, perm: "tools.onboarding" },
  { toolKey: "tool.water_info_sheet",       to: "/tools/water-info-sheet", label: "Water Info Sheet", icon: Droplets, perm: "tools.onboarding" },
  { toolKey: "tool.account_applications",   to: "/account-applications", label: "Account Application", icon: FileSignature, perm: "action.manage_account_applications" },
  { toolKey: "tool.trips",                  to: "/trips", label: "Plan a trip", icon: MapPin, perm: "nav.trips" },
  { toolKey: "tool.analytics",              to: "/tools/analytics", label: "Analytics", icon: BarChart3, perm: "tools.analytics" },
  { toolKey: "tool.ocr",                    to: "/ocr/invoices", label: "Invoice OCR", icon: ScanText, perm: "tools.ocr" },
  { toolKey: "tool.account_app_public",     to: "/apply/account", label: "Public Account Application link", icon: ExternalLink, external: true, perm: "tools.onboarding" },
  { toolKey: "tool.users_roles",            to: "/tools/users-roles", label: "Users & Roles", icon: Users, perm: "tools.users_manager" },
  { toolKey: "tool.trash",                  to: "/trash", label: "Trash", icon: Trash2Icon, perm: "tools.users_manager", roles: ["owner", "admin"] },
  { toolKey: "tool.bug_reports",            to: "/settings/hidro/health", label: "Bug reports", icon: BugIcon, perm: "tools.users_manager", roles: ["owner", "admin"] },
];

// "More" group — auto-collects items that the user has demoted via Settings,
// plus a small fixed set of less-frequent destinations so we don't crowd the
// main sidebar.
const MORE_CHILDREN_BASE = [
  { to: "/clock", label: "Clock in / out", icon: ClockIcon, perm: "nav.dashboard", key: "more.clock" },
  { to: "/map", label: "Map", icon: MapIcon, perm: "nav.customers", key: "more.map" },
];

// ── Phase 8A — 14-item sidebar ───────────────────────────────────────────
// Industry-best shape:
//   4 always-on (Home/Calendar/Inbox/Chat)
//   + 7 pillars (Sales/Service/Team/Operations/Projects/Finance/Insights)
//   + 3 utility (Tools/Settings/Profile)
// Each pillar's children inherit perm-gating — when a tenant doesn't have
// e.g. the Job Cards module (`nav.jobcards = false`), the Job Cards child
// silently disappears from Service.  If a pillar's children all evaluate
// to no-permission, the pillar auto-hides.  This is what enables modular
// pricing without code changes.
const PILLAR_CHILDREN = {
  sales: [
    { to: "/leads", label: "Leads", icon: GitBranch, key: "leads", perm: "nav.leads", module: "sales_flow" },
    { to: "/customers", label: "Customers", icon: Users, key: "customers", perm: "nav.customers", module: "sales_flow" },
    { to: "/performance", label: "Performance", icon: BarChart3, key: "performance", perm: "nav.customers", module: "sales_flow" },
    { to: "/sales-funnel", label: "Sales Funnel", icon: TrendingUp, key: "sales-funnel", perm: "nav.sales_funnel", module: "sales_flow" },
    { to: "/estimator", label: "Estimator", icon: Calculator, key: "estimator", perm: "nav.sales_funnel", module: "sales_flow" },
    { to: "/followups", label: "Follow-ups", icon: BellRing, key: "followups", perm: "nav.leads", module: "sales_flow" },
    { to: "/sales-documents", label: "Quotes & sales orders", icon: ReceiptIcon, key: "sales-documents", perm: "nav.invoices", module: "sales_flow" },
  ],
  service: [
    { to: "/jobcards", label: "Job Cards", icon: ClipboardList, key: "jobcards", perm: "nav.jobcards", module: "ops" },
    { to: "/scheduler", label: "Service Schedule", icon: CalendarClock, key: "scheduler", perm: "nav.schedule", module: "ops" },
    { to: "/service-history", label: "Service History", icon: ClockIcon, key: "service-history", perm: "nav.schedule", module: "ops" },
    { to: "/equipment/all", label: "Equipment", icon: Wrench, key: "equipment-all", perm: "nav.equipment", module: "ops" },
    { to: "/tasks", label: "Tasks", icon: CheckSquare, key: "tasks", perm: "nav.tasks" /* platform-basic always */ },
    // Iter 290zp.16 — Support Tickets moved under Ops per owner directive.
    // The standalone "Support" pillar has been removed from the sidebar.
    // We retain `module: "support"` gating so tenants that don't own the
    // Support module still don't see the row.
    { to: "/tickets", label: "Support Tickets", icon: LifeBuoy, key: "tickets", perm: "nav.tickets", module: "support" },
  ],
  team: [
    { to: "/team", label: "Team home", icon: Users, key: "team", perm: "nav.dashboard", module: "teams" },
    { to: "/team/attendance", label: "Attendance", icon: ClockIcon, key: "team-attendance", perm: "nav.dashboard", module: "teams" },
    { to: "/team/leave", label: "Leave", icon: CalendarDays, key: "team-leave", perm: "nav.dashboard", module: "teams" },
    { to: "/team/performance", label: "Performance", icon: BarChart3, key: "team-performance", perm: "nav.dashboard", module: "teams" },
    { to: "/team/competitions", label: "Competitions", icon: TrendingUp, key: "team-competitions", perm: "nav.dashboard", module: "teams" },
    { to: "/team/scorecard", label: "Scorecard", icon: TrendingUp, key: "team-scorecard", perm: "nav.dashboard", module: "teams" },
    { to: "/recruitment", label: "Recruitment", icon: UserPlus, key: "recruitment", perm: "nav.dashboard", module: "teams" },
    { to: "/contracts", label: "Contracts", icon: FileSignature, key: "contracts", perm: "nav.dashboard", module: "teams" },
  ],
  operations: [
    { to: "/equipment", label: "Inventory", icon: Boxes, key: "inventory", perm: "nav.equipment", module: "stockroom" },
    { to: "/picking", label: "Picking", icon: PackageCheck, key: "picking", perm: "nav.tools", module: "stockroom" },
    { to: "/suppliers", label: "Suppliers", icon: Building2, key: "suppliers", perm: "nav.tools", module: "stockroom" },
    { to: "/order-requests", label: "Order requests", icon: ShoppingCart, key: "orders", perm: "nav.orders", module: "stockroom" },
    { to: "/procurement", label: "Procurement", icon: Boxes, key: "procurement", perm: "nav.procurement", module: "stockroom" },
    // Iter 230 — Trip Planner moved out of Stockroom sidebar (it's a
    // SalesFlow capability per manifest). Now reachable from buttons
    // on the Leads + Customers pages — see Leads.jsx toolbar.
  ],
  projects: [
    { to: "/projects", label: "Projects", icon: Briefcase, key: "projects", perm: "nav.jobcards", module: "projects" },
  ],
  finance: [
    // Same /sales-documents route as Sales but framed for accounting.
    { to: "/sales-documents", label: "Invoices & statements", icon: ReceiptIcon, key: "finance-docs", perm: "nav.invoices", module: "bookkeep" },
    { to: "/ocr/invoices", label: "Invoice OCR", icon: ScanText, key: "ocr", perm: "tools.ocr", module: "bookkeep" },
    { to: "/account-applications", label: "Account applications", icon: FileSignature, key: "account-apps", perm: "action.manage_account_applications", module: "sales_flow" },
  ],
  support: [
    { to: "/tickets", label: "Support Tickets", icon: LifeBuoy, key: "tickets", perm: "nav.tickets", module: "support" },
  ],
  connect: [
    // Iter 231 — Connect pillar: portal-admin hub graduates from
    // /settings/platform to its own surface so the pillar has real content.
    { to: "/portal-admin", label: "Portal admin", icon: Users, key: "portal-admin", perm: "nav.portal_admin", module: "connect" },
  ],
  insights: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, key: "dashboard", perm: "nav.dashboard" /* platform-always */ },
    { to: "/tools/analytics", label: "Analytics", icon: BarChart3, key: "analytics", perm: "tools.analytics" },
    { to: "/tools/engineering-desk", label: "Engineering Desk", icon: FlaskConical, key: "engineering", perm: "nav.tools", module: "ops" },
    { to: "/gallery", label: "Gallery", icon: ImageIcon, key: "gallery", perm: "nav.tools" },
  ],
};

// ── Iter 228 — Sidebar items are now MODULES, labelled with marketing
// names. Each pillar IS a module hub (or a permanent platform surface).
// Order matches what the customer pays for + the manifest doc.
const PILLAR_MODULE = {
  "pillar.salesflow":   "sales_flow",
  "pillar.ops":         "ops",
  "pillar.teams":       "teams",
  "pillar.projects":    "projects",
  "pillar.stockroom":   "stockroom",
  "pillar.bookkeep":    "bookkeep",
  "pillar.support":     "support",
  "pillar.connect":     "connect",
  "pillar.insights":    null,  // always visible (Dashboard is platform-basic)
};

const PILLARS = [
  { key: "pillar.salesflow", label: "SalesFlow", icon: TrendingUp,    children: PILLAR_CHILDREN.sales },
  { key: "pillar.ops",       label: "Ops",       icon: ClipboardList, children: PILLAR_CHILDREN.service },
  { key: "pillar.teams",     label: "Teams",     icon: Users,         children: PILLAR_CHILDREN.team },
  { key: "pillar.projects",  label: "Projects",  icon: Briefcase,     children: PILLAR_CHILDREN.projects },
  { key: "pillar.stockroom", label: "Stockroom", icon: Boxes,         children: PILLAR_CHILDREN.operations },
  { key: "pillar.bookkeep",  label: "Bookkeep",  icon: ReceiptIcon,   children: PILLAR_CHILDREN.finance },
  { key: "pillar.connect",   label: "Connect",   icon: Globe,         children: PILLAR_CHILDREN.connect },
  { key: "pillar.insights",  label: "Insights",  icon: BarChart3,     children: PILLAR_CHILDREN.insights },
];

const NAV = [
  // ── Top (4 always-on) ─────────────────────────────────────────────
  { to: "/", label: "Home", mobileLabel: "Home", icon: Home, end: true, key: "home", perm: "nav.dashboard" },
  { to: "/calendar", label: "Calendar", mobileLabel: "Cal", icon: CalendarDays, key: "calendar", perm: "nav.calendar" },
  { to: "/inbox", label: "Inbox", mobileLabel: "Inbox", icon: InboxIcon, key: "inbox", perm: "nav.dashboard" },
  { to: "/chat", label: "Chat", mobileLabel: "Chat", icon: MessageSquare, key: "chat", perm: "nav.dashboard" },
  // ── 8 module pillars + Insights — each pillar's children are perm-gated
  // individually at render time. The pillar's OWN `perm` is a sentinel
  // that ALWAYS passes hasPermission() so the outer NAV.filter() doesn't
  // accidentally drop pillars for non-owner roles (rep/admin/manager/tech
  // didn't have a `pillar.*` perm key by design — pillars aren't perms,
  // they're containers).  We grant the pillar `perm: "nav.dashboard"`
  // (every staff role has this) and let per-child perm gating do the work.
  ...PILLARS.map((p) => ({
    isPillar: true,
    ...p,
    mobileLabel: p.label.slice(0, 4),
    perm: "nav.dashboard",
  })),
  // ── Footer slot 12: Tools workshop launcher ──────────────────────
  { to: "/tools", label: "Tools", mobileLabel: "Tools", icon: Wrench, key: "tools-workshop", perm: "nav.tools" },
  // ── Footer slots 13 (Settings) + 14 (Profile/Logout) are rendered
  //     below the <nav> map by the existing code; they are not in NAV.
];

// Phase 8A — pillars now own grouping; flat-list hiding is no longer needed.
// Kept as an empty Set so the per-user `hv_nav_hidden` localStorage and the
// downstream `demotedItems` / `moreChildren` machinery stay byte-identical
// (just always-empty) until 8D drops them with the More group.
const DEFAULT_HIDDEN_KEYS = new Set();

// Items that live as icons in the mobile header instead of the bottom nav
// to reduce clutter on phones.
const MOBILE_HEADER_KEYS = new Set(["home", "calendar"]);

// Map each top-level URL to the permission that gates deep-linking to it.
// Routes not in this map require only login (coarse checks stay in place).
export const PATH_PERMISSION_MAP = {
  "/": "nav.dashboard",
  "/dashboard": "nav.dashboard",
  "/home": "nav.dashboard",
  "/calendar": "nav.calendar",
  "/tasks": "nav.tasks",
  "/calls": "nav.calls",
  "/customers": "nav.customers",
  "/leads": "nav.leads",
  "/sales-funnel": "nav.sales_funnel",
  "/estimator": "nav.sales_funnel",
  "/jobcards": "nav.jobcards",
  "/projects": "nav.jobcards",
  "/team": "nav.dashboard",
  "/me": "nav.dashboard",
  "/clock": "nav.dashboard",
  "/team/performance": "nav.dashboard",
  "/team/announcements": "nav.dashboard",
  "/team/competitions": "nav.dashboard",
  "/team/scorecard": "nav.dashboard",
  "/recruitment": "nav.dashboard",
  "/tools/import": "nav.dashboard",
  "/team/attendance": "nav.dashboard",
  "/team/leave": "nav.dashboard",
  "/contracts": "nav.dashboard",
  "/tickets": "nav.tickets",
  "/scheduler": "nav.schedule",
  "/service-history": "nav.schedule",
  "/analytics": "nav.analytics",
  "/ocr/invoices": "tools.ocr",
  "/account-applications": "action.manage_account_applications",
  "/procurement": "nav.procurement",
  "/order-requests": "nav.orders",
  "/sales-documents": "nav.invoices",
  "/equipment": "nav.equipment",
  "/tools/users-roles": "tools.users_manager",
  "/tools/inventory-categories": "tools.inventory_categories",
};

export default function Layout() {
  const { user, logout, hasPermission } = useAuth();
  const { branding: layoutBranding, modules: tenantModules } = useBranding();
  const { theme: layoutTheme } = useTheme();
  // ── Phase 8.6 — pillar visibility gated by module ownership ──────
  // A pillar with a `module` in PILLAR_MODULE only renders when that
  // module is owned by the tenant. Children with a `module` field
  // additionally self-gate.
  const hasModule = (modKey) => {
    if (!modKey) return true;
    if (!tenantModules || Object.keys(tenantModules).length === 0) return true; // hydrating
    return tenantModules[modKey] !== false;
  };
  const _isHV = typeof window !== "undefined" &&
    ((window.location.host || "").toLowerCase().includes("hidroverse"));
  const _fallbackLogo = _isHV
    ? "https://customer-assets.emergentagent.com/job_tech-dispatch-55/artifacts/qjyr3rrs_hidroverse%20%285%29.png"
    : "/brand/rogue-white-blue.png";
  // [Iter 290zp.13t] Theme-aware logo. The dark logo variant is
  // white-on-transparent and disappears against a white sidebar in
  // light mode. Branch on the active theme to pick the right asset,
  // falling back to logo_url for tenants that haven't uploaded the
  // theme-specific variants yet.
  const SIDEBAR_LOGO =
    (layoutTheme === "light"
      ? (layoutBranding?.logo_url_light || layoutBranding?.logo_url)
      : (layoutBranding?.logo_url_dark || layoutBranding?.logo_url))
    || _fallbackLogo;
  const SIDEBAR_BRAND = layoutBranding?.app_name || (_isHV ? "SalesFlow" : "Rogue");
  const navigate = useNavigate();
  const location = useLocation();
  // Wave 19 #89 / Wave 26.7 — hide the priority banner + bottom mobile nav
  // on full-screen "chat-style" routes so the soft keyboard + composer can
  // own the bottom of the viewport (`/chat/:tid` and the deep-reasoning
  // engineering session at `/tools/engineering-desk/:sid` — but NOT the
  // engineering-desk index, library list, or library detail).
  const isFullscreenChat = (
    /^\/chat\/[^/]+/.test(location.pathname) ||
    /^\/tools\/engineering-desk\/(?!library(?:\/|$))[^/]+/.test(location.pathname)
  );
  // Keep the legacy alias so the rest of this file (~10 references) keeps
  // working. They all gate the same behaviour: hide bottom nav, fullscreen,
  // banner-off.
  const isChatDetail = isFullscreenChat;

  // Per-user hidden nav set — items moved into the "More" group via
  // Settings → Menu customisation. Defaults to DEFAULT_HIDDEN_KEYS so the
  // primary sidebar stays uncluttered.
  const [hiddenKeys, setHiddenKeys] = useState(() => {
    try {
      const saved = localStorage.getItem("hv_nav_hidden");
      if (saved) return new Set(JSON.parse(saved));
    } catch (_e) { /* ignore */ }
    return new Set(DEFAULT_HIDDEN_KEYS);
  });
  // Wave 19 #108 — Coach modal listens for the global open event so any
  // surface (trip-debrief commit, post-trip notification, dashboard
  // shortcut) can open it without prop-drilling.
  const [coachOpen, setCoachOpen] = useState(false);
  useEffect(() => {
    const handler = () => setCoachOpen(true);
    window.addEventListener("hv:open-coach", handler);
    return () => window.removeEventListener("hv:open-coach", handler);
  }, []);

  // Per-user tool customisations.
  //   promotedTools = set of tool.keys that have been promoted to top-level nav
  //   demotedTools  = set of tool.keys that have been hidden under "More"
  // Everything else stays under the Tools group (default).
  const [promotedTools, setPromotedTools] = useState(() => {
    try {
      const saved = localStorage.getItem("hv_tools_promoted");
      if (saved) return new Set(JSON.parse(saved));
    } catch (_e) { /* ignore */ }
    return new Set();
  });
  const [demotedTools, setDemotedTools] = useState(() => {
    try {
      const saved = localStorage.getItem("hv_tools_demoted");
      if (saved) return new Set(JSON.parse(saved));
    } catch (_e) { /* ignore */ }
    return new Set();
  });
  // Listen for cross-component updates (from Settings).
  useEffect(() => {
    const onUpdate = (e) => {
      try {
        const next = e?.detail?.hidden;
        if (Array.isArray(next)) setHiddenKeys(new Set(next));
        const tp = e?.detail?.toolsPromoted;
        if (Array.isArray(tp)) setPromotedTools(new Set(tp));
        const td = e?.detail?.toolsDemoted;
        if (Array.isArray(td)) setDemotedTools(new Set(td));
      } catch (_e) { /* noop */ }
    };
    window.addEventListener("hv:nav:hidden:changed", onUpdate);
    return () => window.removeEventListener("hv:nav:hidden:changed", onUpdate);
  }, []);

  // Iter 228 — fetch the user's saved nav preferences (bottom-nav order
  // + Quick Actions order). Falls back to role defaults when empty.
  const [navPrefs, setNavPrefs] = useState({ bottom_nav: [], quick_actions: [] });
  useEffect(() => {
    if (!user) return;
    let alive = true;
    api.get("/me/prefs/nav").then(({ data }) => {
      if (alive) setNavPrefs(data || { bottom_nav: [], quick_actions: [] });
    }).catch(() => { /* default to role-based */ });
    const onUpdate = (e) => {
      if (e?.detail) setNavPrefs(e.detail);
    };
    window.addEventListener("hv:nav-prefs:updated", onUpdate);
    return () => {
      alive = false;
      window.removeEventListener("hv:nav-prefs:updated", onUpdate);
    };
  }, [user?.id]);

  const baseVisibleNav = NAV.filter((n) => hasPermission(n.perm));
  const primaryNav = baseVisibleNav.filter((n) => n.isGroup || !hiddenKeys.has(n.key));
  const demotedItems = baseVisibleNav.filter((n) => !n.isGroup && hiddenKeys.has(n.key));

  // Wave 7-fix #7 — user-customisable nav order. Persist to localStorage
  // (per-device) AND to user profile (cross-device, set on next save).
  const [navOrder, setNavOrder] = useState(() => {
    try {
      const saved = localStorage.getItem("hv_nav_order");
      if (saved) return JSON.parse(saved);
    } catch (_e) { /* ignore */ }
    return user?.preferences?.nav_order || primaryNav.map((n) => n.key);
  });
  // Drag state
  const [dragKey, setDragKey] = useState(null);

  // Wave 7-fix #2 — Tools fold-out state. Persists per-user via preferences.
  const [toolsOpen, setToolsOpen] = useState(
    () => !!user?.preferences?.tools_open,
  );
  const toggleTools = () => {
    const next = !toolsOpen;
    setToolsOpen(next);
    api.patch("/users/me/preferences", { tools_open: next }).catch(() => {});
  };

  // "More" fold-out state — purely local (per device).
  const [moreOpen, setMoreOpen] = useState(() => localStorage.getItem("hv_more_open") === "1");
  const toggleMore = () => {
    const next = !moreOpen;
    setMoreOpen(next);
    try { localStorage.setItem("hv_more_open", next ? "1" : "0"); } catch (_e) { /* noop */ }
  };

  // Iter 230 — Per-pillar collapse state with EXPLICIT close model.
  // Previous bug: `pillarsOpen ∪ pillarContainsRoute` meant a pillar
  // containing the active route could NEVER be collapsed (the OR kept
  // it open even after the user clicked to close). Fix: track an
  // explicit `pillarsClosed` set. Default behaviour:
  //   - Pillar containing active route → open (auto)
  //   - Pillar with no active child but in `pillarsOpen` → open (user-expanded)
  //   - Pillar in `pillarsClosed` → forced closed (user clicked it shut)
  // Clicking a header toggles between the user's two explicit states.
  const [pillarsOpen, setPillarsOpen] = useState(() => {
    try {
      const raw = localStorage.getItem("hv_pillars_open");
      if (raw) return new Set(JSON.parse(raw));
    } catch (_e) { /* noop */ }
    return new Set();
  });
  const [pillarsClosed, setPillarsClosed] = useState(() => {
    try {
      const raw = localStorage.getItem("hv_pillars_closed");
      if (raw) return new Set(JSON.parse(raw));
    } catch (_e) { /* noop */ }
    return new Set();
  });
  const togglePillar = (key) => {
    // Determine current visible state to decide which direction to flip.
    const containsActive = visiblePillarContainsRoute(key);
    const currentlyOpen = pillarsClosed.has(key)
      ? false
      : pillarsOpen.has(key) || containsActive;
    if (currentlyOpen) {
      // Force close: add to closed set, remove from open set.
      setPillarsOpen((p) => {
        const next = new Set(p); next.delete(key);
        try { localStorage.setItem("hv_pillars_open", JSON.stringify([...next])); } catch (_e) { /* noop */ }
        return next;
      });
      setPillarsClosed((p) => {
        const next = new Set(p); next.add(key);
        try { localStorage.setItem("hv_pillars_closed", JSON.stringify([...next])); } catch (_e) { /* noop */ }
        return next;
      });
    } else {
      // Force open: remove from closed set, add to open set.
      setPillarsClosed((p) => {
        const next = new Set(p); next.delete(key);
        try { localStorage.setItem("hv_pillars_closed", JSON.stringify([...next])); } catch (_e) { /* noop */ }
        return next;
      });
      setPillarsOpen((p) => {
        const next = new Set(p); next.add(key);
        try { localStorage.setItem("hv_pillars_open", JSON.stringify([...next])); } catch (_e) { /* noop */ }
        return next;
      });
    }
  };
  const pillarContainsRoute = (pillar) =>
    pillar.children.some((c) => location.pathname === c.to || location.pathname.startsWith(c.to + "/"));
  const visiblePillarContainsRoute = (pillarKey) => {
    const p = PILLARS.find((x) => x.key === pillarKey);
    return p ? pillarContainsRoute(p) : false;
  };
  // Filter TOOLS children based on per-user promote/demote
  const toolsEffective = TOOLS_CHILDREN.filter(
    (c) => !promotedTools.has(c.toolKey) && !demotedTools.has(c.toolKey),
  );
  const toolsPromotedItems = TOOLS_CHILDREN.filter((c) => promotedTools.has(c.toolKey));
  const toolsDemotedItems = TOOLS_CHILDREN.filter((c) => demotedTools.has(c.toolKey));

  const moreChildren = [
    ...MORE_CHILDREN_BASE.filter((c) => hasPermission(c.perm)),
    ...demotedItems.map((d) => ({ to: d.to, label: d.label, icon: d.icon, perm: d.perm, key: `more.${d.key}` })),
    ...toolsDemotedItems.map((c) => ({ to: c.to, label: c.label, icon: c.icon, perm: c.perm, key: `more.${c.toolKey}`, external: c.external })),
  ];

  // Apply order, append any nav items missing from the saved order (e.g. new
  // routes added since the user last saved).
  const visibleNav = (() => {
    const map = Object.fromEntries(primaryNav.map((n) => [n.key, n]));
    const ordered = navOrder.map((k) => map[k]).filter(Boolean);
    const orderedSet = new Set(ordered.map((n) => n.key));
    const rest = primaryNav.filter((n) => !orderedSet.has(n.key));
    return [...ordered, ...rest];
  })();

  const persistOrder = (next) => {
    setNavOrder(next);
    try { localStorage.setItem("hv_nav_order", JSON.stringify(next)); } catch (_e) { /* ignore */ }
    // Server-side persistence — fire and forget.
    api.patch("/users/me/preferences", { nav_order: next }).catch(() => {});
  };

  // Iter 228 — Mobile bottom-nav defaults are now ROLE-AWARE and
  // user-customisable via /settings/profile (stored in user.prefs.nav).
  // Pure navigation surfaces only — Quick Actions live in the ⊕ launcher
  // (top banner + mobile header), not as a bottom-nav slot.
  const mobileBottomNav = (() => {
    const role = user?.role;
    const can = (p) => hasPermission(p);
    const Mod = (m) => hasModule(m);
    const slot = {
      home:       { to: "/", label: "Home", mobileLabel: "Home", icon: Home, key: "home", end: true },
      jobcards:   { to: "/jobcards", label: "Jobs", mobileLabel: "Jobs", icon: ClipboardList, key: "jobcards" },
      tickets:    { to: "/tickets", label: "Tickets", mobileLabel: "Tickets", icon: LifeBuoy, key: "tickets" },
      tasks:      { to: "/tasks", label: "Tasks", mobileLabel: "Tasks", icon: CheckSquare, key: "tasks" },
      customers:  { to: "/customers", label: "Customers", mobileLabel: "Custs", icon: Users, key: "customers" },
      calendar:   { to: "/calendar", label: "Calendar", mobileLabel: "Cal", icon: CalendarDays, key: "calendar" },
      chat:       { to: "/chat", label: "Chat", mobileLabel: "Chat", icon: MessageSquare, key: "chat" },
      leads:      { to: "/leads", label: "Leads", mobileLabel: "Leads", icon: GitBranch, key: "leads" },
      pipeline:   { to: "/sales-funnel", label: "Pipeline", mobileLabel: "Pipe", icon: TrendingUp, key: "pipeline" },
      projects:   { to: "/projects", label: "Projects", mobileLabel: "Proj", icon: Briefcase, key: "projects" },
      picking:    { to: "/picking", label: "Picking", mobileLabel: "Pick", icon: PackageCheck, key: "picking" },
      inventory:  { to: "/equipment", label: "Inventory", mobileLabel: "Stock", icon: Boxes, key: "inventory" },
      suppliers:  { to: "/suppliers", label: "Suppliers", mobileLabel: "Sup", icon: Building2, key: "suppliers" },
      orders:     { to: "/order-requests", label: "Orders", mobileLabel: "Ord", icon: ShoppingCart, key: "orders" },
      invoices:   { to: "/sales-documents", label: "Invoices", mobileLabel: "Invs", icon: ReceiptIcon, key: "invoices" },
    };
    // Role-aware default 6-slot stack (sequence = priority).
    let preferred = [];
    if (role === "technician") {
      preferred = ["jobcards", "tickets", "tasks", "customers", "calendar", "chat"];
    } else if (role === "rep" || role === "sales") {
      preferred = ["leads", "pipeline", "customers", "tasks", "calendar", "chat"];
    } else if (role === "admin") {
      preferred = ["tickets", "jobcards", "customers", "tasks", "calendar", "chat"];
    } else if (role === "manager") {
      preferred = ["jobcards", "tickets", "leads", "customers", "tasks", "calendar"];
    } else if (role === "procurement") {
      preferred = ["picking", "inventory", "suppliers", "orders", "tasks", "calendar"];
    } else {
      // owner / fallback — Home is always first so the owner can never
      // get marooned in a deep route (Dirk: "there's no home button.
      // It's like the app is completely broken"). The PDF viewer's
      // Back button rewinds one step in history; if that lands in a
      // sub-page with no Home in the bottom nav, the only way out is
      // force-quitting the PWA. Home in slot 1 ends that trap.
      preferred = Mod("projects")
        ? ["home", "projects", "tickets", "customers", "tasks", "chat"]
        : ["home", "tickets", "customers", "tasks", "calendar", "chat"];
    }
    // Override with user preferences from /api/me/prefs/nav when set
    const override = Array.isArray(navPrefs?.bottom_nav) && navPrefs.bottom_nav.length >= 4
      ? navPrefs.bottom_nav
      : null;
    const order = override || preferred;
    // Resolve to slot defs, filter by perm + module
    const PERM = {
      jobcards: "nav.jobcards", tickets: "nav.tickets", tasks: "nav.tasks",
      customers: "nav.customers", calendar: "nav.calendar", chat: "nav.dashboard",
      leads: "nav.leads", pipeline: "nav.sales_funnel", projects: "nav.jobcards",
      picking: "nav.tools", inventory: "nav.equipment", suppliers: "nav.tools",
      orders: "nav.orders", invoices: "nav.invoices", home: "nav.dashboard",
    };
    const MOD = {
      jobcards: "ops", tickets: "support", leads: "sales_flow", pipeline: "sales_flow",
      projects: "projects", picking: "stockroom", inventory: "stockroom",
      suppliers: "stockroom", orders: "stockroom", invoices: "bookkeep",
    };
    return order
      .map((key) => slot[key])
      .filter(Boolean)
      .filter((s) => can(PERM[s.key] || "nav.dashboard") && Mod(MOD[s.key] || null))
      .slice(0, 5);
  })();
  // The icons we surface in the mobile header (only those the role can see).
  const mobileHeaderNav = visibleNav.filter((n) => MOBILE_HEADER_KEYS.has(n.key));

  // ── Mobile "More" drawer state + sections ────────────────────────
  // The 6th slot on the mobile bottom nav opens a full-height drawer
  // that exposes EVERY page from the desktop sidebar — driven from the
  // same `PILLARS` + `TOOLS_CHILDREN` data. Adding a page to a pillar
  // is the only step required for it to appear on mobile.
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const moreSections = useMemo(() => {
    const sections = [];
    // 1) Always-on top items (Home / Calendar / Inbox / Chat)
    const mainItems = [
      { to: "/", label: "Home", icon: Home, key: "more-home", perm: "nav.dashboard" },
      { to: "/calendar", label: "Calendar", icon: CalendarDays, key: "more-calendar", perm: "nav.calendar" },
      { to: "/inbox", label: "Inbox", icon: InboxIcon, key: "more-inbox", perm: "nav.dashboard" },
      { to: "/chat", label: "Chat", icon: MessageSquare, key: "more-chat", perm: "nav.dashboard" },
    ].filter((it) => hasPermission(it.perm));
    if (mainItems.length) {
      sections.push({ key: "main", label: "Main", icon: Home, items: mainItems });
    }
    // 2) Each pillar as a section (perm + module gated, identical to sidebar)
    PILLARS.forEach((p) => {
      if (!hasModule(PILLAR_MODULE[p.key])) return;
      const items = p.children
        .filter((c) => hasPermission(c.perm) && hasModule(c.module))
        .map((c) => ({ to: c.to, label: c.label, icon: c.icon, key: c.key }));
      if (items.length) sections.push({ key: p.key, label: p.label, icon: p.icon, items });
    });
    // 3) Tools (perm-gated)
    const toolItems = TOOLS_CHILDREN
      .filter((t) => hasPermission(t.perm))
      .map((t) => ({
        to: t.to,
        label: t.label,
        icon: t.icon,
        key: `tools-${t.toolKey}`,
        external: t.external,
      }));
    if (toolItems.length) {
      sections.push({ key: "tools", label: "Tools", icon: Wrench, items: toolItems });
    }
    // 4) Account utilities (Clock, Map, Settings)
    const utilityItems = [
      { to: "/clock", label: "Clock in / out", icon: ClockIcon, key: "util-clock", perm: "nav.dashboard" },
      { to: "/map", label: "Map", icon: MapIcon, key: "util-map", perm: "nav.customers" },
      { to: "/settings", label: "Settings", icon: Settings, key: "util-settings", perm: "nav.dashboard" },
    ].filter((it) => hasPermission(it.perm));
    if (utilityItems.length) {
      sections.push({ key: "account", label: "Account", icon: Settings, items: utilityItems });
    }
    return sections;
  }, [hasPermission, tenantModules]); // eslint-disable-line react-hooks/exhaustive-deps

  const { newCount, markAsSeen } = useTicketNotifications({
    userId: user?.id,
    role: user?.role,
  });

  // Global haptic feedback delegate — fires a tiny vibration whenever the
  // user taps a primary CTA. Cheap to attach once, no per-site changes.
  useEffect(() => {
    const onPointerDown = (e) => {
      const btn = e.target?.closest?.("button, a");
      if (!btn) return;
      if (
        btn.classList?.contains("hv-btn-primary") ||
        btn.dataset?.testid?.endsWith("-submit") ||
        btn.dataset?.testid?.endsWith("-save")
      ) {
        tap();
      }
    };
    window.addEventListener("pointerdown", onPointerDown, true);
    return () => window.removeEventListener("pointerdown", onPointerDown, true);
  }, []);
  // Mobile column count maps to visible nav size (Tailwind needs literal class names)
  const MOBILE_COLS = {
    1: "grid-cols-1",
    2: "grid-cols-2",
    3: "grid-cols-3",
    4: "grid-cols-4",
    5: "grid-cols-5",
    6: "grid-cols-6",
    7: "grid-cols-7",
    8: "grid-cols-8",
  };
  // The bottom nav always shows N dynamic items + 1 "More" toggle, so
  // grid columns = items.length + 1.
  const mobileCols = MOBILE_COLS[mobileBottomNav.length + 1] || "grid-cols-6";

  // Iter 290zo — hide bottom-nav while the on-screen keyboard is open
  // on mobile. Cures the "nav stuck mid-screen" bug caused by the gap
  // between layout-viewport and visual-viewport when soft keyboards
  // appear/disappear (position:fixed anchors to layout viewport, not
  // visual one, on most mobile browsers).
  const kbOpen = useKeyboardOpen();

  // Iter 290zo — apply a body class so sibling fixed-bottom widgets
  // (bug-report button, version pill, install prompt, upload dock) can
  // hide themselves via plain CSS without needing the hook each.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const cls = "hv-kb-open";
    if (kbOpen) document.body.classList.add(cls);
    else document.body.classList.remove(cls);
    return () => document.body.classList.remove(cls);
  }, [kbOpen]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <>
      <DemoBanner />
      <div className={`${isChatDetail ? "h-screen overflow-hidden" : "min-h-screen"} flex`} data-testid="app-layout">
      {/* Desktop sidebar — also hidden in short landscape (phone rotated
          horizontally) so users get a full-screen work area, Iter 385. */}
      {/* Iter 398h — Dirk on iPad PWA: "I've got the large menu at
          the left, so a very small screen real estate." The sidebar
          breakpoint was `md` (768px), which meant iPad portrait
          (834px) still rendered the fixed 256px sidebar. Bumped to
          `lg` (1024px) so iPad portrait + iPad mini + iPad Pro
          portrait all fall back to the mobile bottom-nav layout,
          giving techs the full-width real estate they need on-site.
          Landscape iPad Pro (1194px) keeps the sidebar. */}
      <aside className="hv-sidebar hidden lg:flex flex-col w-64 bg-[#0a0b0e] border-r border-white/10 sticky top-0 h-screen">
        <div className="px-5 py-6 border-b border-white/10 flex flex-col items-center gap-2">
          <img
            src={SIDEBAR_LOGO}
            alt={SIDEBAR_BRAND}
            className="h-24 w-auto object-contain"
          />
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
          {visibleNav.map((n) => {
            // ----- Phase 8A + 8.6: Pillar group (fold-out, perm + module gated) -----
            if (n.isPillar) {
              // Phase 8.6 — first gate: pillar-level module ownership.
              const pillarModuleKey = PILLAR_MODULE[n.key];
              if (!hasModule(pillarModuleKey)) return null;
              // Then: filter children by EACH child's perm AND module.
              const visibleChildren = n.children.filter(
                (c) => hasPermission(c.perm) && hasModule(c.module),
              );
              // Hide pillar entirely if no children are permitted — this is
              // the mechanism that hides modules the tenant hasn't bought.
              if (visibleChildren.length === 0) return null;
              const isOpen = pillarsClosed.has(n.key)
                ? false
                : (pillarsOpen.has(n.key) || pillarContainsRoute(n));
              const hasActiveChild = pillarContainsRoute(n);
              return (
                <div key={n.key} className="space-y-1">
                  <button
                    type="button"
                    onClick={() => togglePillar(n.key)}
                    data-testid={`nav-${n.key.replace(".", "-")}-toggle`}
                    className={`w-full relative flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition border ${
                      hasActiveChild
                        ? "bg-[#1fb6c1]/10 border-[#1fb6c1]/30 text-[#1fb6c1]"
                        : isOpen
                          ? "bg-white/[0.03] border-white/10 text-white"
                          : "text-[#a0aabf] hover:text-white hover:bg-white/5 border-transparent"
                    }`}
                  >
                    <span className="text-[#647087] opacity-40 text-[10px] font-mono select-none">⋮⋮</span>
                    <n.icon className="w-4 h-4" strokeWidth={1.6} />
                    <span className="flex-1 text-left">{n.label}</span>
                    <span className="text-[10px] text-[#647087]">{visibleChildren.length}</span>
                    {isOpen ? (
                      <ChevronDown className="w-3.5 h-3.5 text-[#647087]" />
                    ) : (
                      <ChevronRight className="w-3.5 h-3.5 text-[#647087]" />
                    )}
                  </button>
                  {isOpen && (
                    <div
                      className="ml-3 pl-3 border-l border-white/5 space-y-0.5"
                      data-testid={`nav-${n.key.replace(".", "-")}-children`}
                    >
                      {visibleChildren.map((c) => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          data-testid={`nav-${n.key.replace(".", "-")}-${c.key}`}
                          className={({ isActive }) =>
                            `flex items-center gap-2 px-3 py-2 rounded-md text-xs transition ${
                              isActive
                                ? "bg-[#1fb6c1]/10 text-[#1fb6c1] border border-[#1fb6c1]/30"
                                : "text-[#a0aabf] hover:text-white hover:bg-white/5 border border-transparent"
                            }`
                          }
                        >
                          <c.icon className="w-3.5 h-3.5" strokeWidth={1.6} />
                          {c.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            // ----- Regular top-level nav item -----
            const isTickets = n.key === "tickets";
            const showBadge = isTickets && newCount > 0;
            const isDragging = dragKey === n.key;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-${n.key}`}
                draggable
                onDragStart={(e) => {
                  setDragKey(n.key);
                  e.dataTransfer.effectAllowed = "move";
                }}
                onDragOver={(e) => {
                  if (dragKey && dragKey !== n.key) {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                  }
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (!dragKey || dragKey === n.key) return;
                  const next = visibleNav.map((x) => x.key).filter((k) => k !== dragKey);
                  const idx = next.indexOf(n.key);
                  next.splice(idx, 0, dragKey);
                  persistOrder(next);
                  setDragKey(null);
                }}
                onDragEnd={() => setDragKey(null)}
                onClick={() => {
                  if (isTickets) markAsSeen();
                }}
                className={({ isActive }) =>
                  `relative flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition cursor-grab active:cursor-grabbing ${
                    isActive
                      ? "bg-[#1fb6c1]/10 text-[#1fb6c1] border border-[#1fb6c1]/30"
                      : "text-[#a0aabf] hover:text-white hover:bg-white/5 border border-transparent"
                  } ${isDragging ? "opacity-40" : ""}`
                }
              >
                <span
                  className="text-[#647087] opacity-40 hover:opacity-100 cursor-grab text-[10px] font-mono select-none"
                  title="Drag to reorder"
                >
                  ⋮⋮
                </span>
                <n.icon className="w-4 h-4" strokeWidth={1.6} />
                {n.label}
                {showBadge && (
                  <span
                    className="ml-auto bg-rose-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center hv-flicker"
                    data-testid="nav-tickets-badge"
                    title={`${newCount} new ticket${newCount === 1 ? "" : "s"}`}
                  >
                    {newCount}
                  </span>
                )}
              </NavLink>
            );
          })}
          {user && ["owner", "admin"].includes(user.role) && (
            <NavLink
              to="/tools/import"
              data-testid="nav-data-import"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition ${
                  isActive
                    ? "bg-[#1fb6c1]/10 text-[#1fb6c1] border border-[#1fb6c1]/30"
                    : "text-[#a0aabf] hover:text-white hover:bg-white/5 border border-transparent"
                }`
              }
            >
              <Database className="w-4 h-4" strokeWidth={1.6} />
              Data Import
            </NavLink>
          )}
          {user && (
            <NavLink
              to="/settings"
              data-testid="nav-settings"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition ${
                  isActive
                    ? "bg-[#1fb6c1]/10 text-[#1fb6c1] border border-[#1fb6c1]/30"
                    : "text-[#a0aabf] hover:text-white hover:bg-white/5 border border-transparent"
                }`
              }
            >
              <Settings className="w-4 h-4" strokeWidth={1.6} />
              Settings
            </NavLink>
          )}
        </nav>

        <div className="border-t border-white/10 px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2.5 min-w-0 flex-1">
              <Avatar user={user} size="sm" />
              <div className="min-w-0">
                <div className="text-sm text-white truncate">{user?.name}</div>
                <div className="text-xs text-[#647087] uppercase tracking-wider">{user?.role}</div>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                data-testid="logout-button"
                onClick={handleLogout}
                className="hv-btn hv-btn-ghost px-2 py-1.5"
                title="Log out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className={`flex-1 min-w-0 lg:pb-0 ${isChatDetail ? "pb-0 flex flex-col h-screen lg:h-auto" : "pb-[calc(5rem+env(safe-area-inset-bottom))]"}`}>
        {/* Global broadcast banner (rendered above sticky headers, dismissable) */}
        <BulletinBanner />
        {/* Desktop top utility bar — sync · quick actions ⊕ · bell · sign-out */}
        <div className="hidden lg:flex items-center gap-2 sticky top-0 z-[60] bg-[#0a0b0e]/80 backdrop-blur-md border-b border-white/5 px-6 lg:px-8 py-2">
          <NavHistoryButtons />
          <div className="flex-1" />
          <OfflineIndicator />
          <QuickActionsLauncher testId="quick-actions-launcher-desktop" />
          <SyncButton />
          <HidroLauncherButton className="px-2 py-1.5" iconClassName="w-4 h-4" testId="hidro-launcher-desktop" />
          <NotificationsBell enabled={!!user} />
          <button
            onClick={handleLogout}
            data-testid="logout-button-desktop"
            className="p-1.5 rounded text-[#a0aabf] hover:text-white hover:bg-white/5 transition-colors"
            title="Log out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
        {/* Mobile header — logo + global nav (back/fwd/home) + Hidro + minimal utilities */}
        <header className="hv-topbar-mobile lg:hidden sticky top-0 z-[60] bg-[#121419]/95 backdrop-blur-xl border-b border-white/10 px-3 py-2 flex items-center gap-1.5 hv-safe-top hv-safe-x">
          <NavLink
            to="/"
            end
            className="flex items-center pl-1"
            data-testid="mobile-home-link"
          >
            <img
              src={SIDEBAR_LOGO}
              alt={SIDEBAR_BRAND}
              className="h-6 w-auto object-contain"
            />
          </NavLink>

          {/* Global navigation buttons — sits between logo and Hidro launcher */}
          <NavHistoryButtons />

          <HidroLauncherButton
            className="w-7 h-7"
            iconClassName="w-4 h-4"
            testId="hidro-launcher-mobile"
          />

          {/* Right-side: only the truly essential utilities. Settings / sync
              / logout all live in the profile drawer / sidebar footer now. */}
          <div className="flex items-center gap-0.5 ml-auto">
            <QuickActionsLauncher testId="quick-actions-launcher-mobile" compact />
            <SyncButton compact />
            <NotificationsBell enabled={!!user} />
            <NavLink
              to="/settings"
              data-testid="mobile-header-profile"
              className="p-1"
              title="My profile"
            >
              <Avatar user={user} size="xs" />
            </NavLink>
            <button
              type="button"
              onClick={handleLogout}
              data-testid="logout-button-mobile"
              className="p-1.5 text-[#a0aabf] hover:text-rose-300 transition"
              title="Sign out"
              aria-label="Sign out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </header>

        {!(isChatDetail) && <EntityTrail />}

        <div className={`hv-fade-in ${isChatDetail ? "p-0 md:p-6 lg:p-8 flex-1 min-h-0 flex flex-col" : "p-4 md:p-6 lg:p-8"}`} data-testid="page-content">
          {user?.role !== "client" && ["/", "/home", "/dashboard"].includes(location.pathname) && <ScorecardBanner />}
          {(user?.role === "owner" || user?.role === "admin" || user?.role === "manager" || user?.role === "rep") && !isChatDetail && (
            <div className="mb-4" data-testid="global-priority-banner-wrap">
              <PriorityAlertBanner />
            </div>
          )}
          {/* Iter 169 — clock-in/out reminder. Self-gates on role + SAST window. */}
          {!isChatDetail && <ClockReminderBanner />}
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
          <PoweredByFooter />
        </div>

        <CommandPalette />

        {/* Iter 290zp.13z21 — GlobalDocDropZone removed. Drops are now
            handled by per-surface dropzones (AccountBlock category
            cards, document tabs) which preserve customer/category
            context. The old global overlay would lock the app if an
            upload hung, and routed drops to /ocr/invoices unlinked. */}

        {/* Mobile bottom nav — hidden on chat-detail so the keyboard +
            composer can use the full viewport. WhatsApp does the same:
            once you're inside a thread the global tabs go away. The user
            taps the back arrow to return to /chat where the nav reappears. */}
        <nav className={`hv-bottomnav lg:hidden hv-hide-on-kb fixed bottom-0 inset-x-0 z-30 bg-[#121419]/95 backdrop-blur-xl border-t border-white/10 hv-safe-bottom hv-safe-x ${isChatDetail || kbOpen ? "hidden" : ""}`}>
          <div className={`grid ${mobileCols} hv-keep-cols`}>
            {mobileBottomNav.map((n) => {
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  data-testid={`nav-mobile-${n.key}`}
                  className={({ isActive }) =>
                    `flex flex-col items-center gap-1 py-2.5 text-[9px] uppercase tracking-wider ${
                      isActive ? "text-[#1fb6c1]" : "text-[#647087]"
                    }`
                  }
                >
                  <n.icon className="w-5 h-5" strokeWidth={1.6} />
                  <span className="leading-none">{n.mobileLabel || n.label}</span>
                </NavLink>
              );
            })}
            {/* 6th slot: opens the More drawer (every other page lives here) */}
            <button
              type="button"
              onClick={() => setMobileMoreOpen(true)}
              data-testid="nav-mobile-more"
              aria-label="Open more navigation"
              className={`flex flex-col items-center gap-1 py-2.5 text-[9px] uppercase tracking-wider ${
                mobileMoreOpen ? "text-[#1fb6c1]" : "text-[#647087]"
              }`}
            >
              <MenuIcon className="w-5 h-5" strokeWidth={1.6} />
              <span className="leading-none">More</span>
            </button>
          </div>
        </nav>
        {/* Mobile More drawer — full-height slide-up, mirrors desktop sidebar tree */}
        <MobileMoreDrawer
          open={mobileMoreOpen}
          onClose={() => setMobileMoreOpen(false)}
          sections={moreSections}
        />
      </main>

      {/* Wave 19 #108 — Coach check-in modal mounted once for the whole app */}
      {!!user && (
        <CoachCheckinModal
          open={coachOpen}
          onClose={() => setCoachOpen(false)}
          onSubmitted={() => { /* pill polls nudge-status hourly; no extra refresh needed */ }}
        />
      )}
      {/* Wave 20 #114 — CONTRACTS §20 global Hidro bug report (auth-only). */}
      {user && <GlobalReportBugButton />}
      {/* [Iter 339] Wrap UploadDock in an ErrorBoundary so a bad
          persisted item (legacy corrupt IndexedDB row) can never take
          down the whole app when the user taps the failed-uploads
          banner. Falls back to a bare dismiss-all button. */}
      {user && (
        <ErrorBoundary fallback={<UploadDockCrashFallback />}>
          <UploadDock />
        </ErrorBoundary>
      )}
    </div>
    </>
  );
}

/* ---------------- "Powered by" footer (Gemini Space shell) ----------
 * Renders only when the active tenant has either:
 *   - branding.powered_by_visible === true AND modules.gemini_shell === true
 *   - or branding.footer_text is set (free-text override)
 * Hidden by default on hidroverse so SalesFlow stays single-brand.
 * ------------------------------------------------------------------- */
function PoweredByFooter() {
  const { branding, modules } = useBranding();
  const customText = branding?.footer_text;
  const showPoweredBy =
    !!branding?.powered_by_visible && !!modules?.gemini_shell;
  if (!customText && !showPoweredBy) return null;
  return (
    <footer
      className="mt-8 pt-4 pb-2 text-center text-[10px] text-[#647087] tracking-[0.2em] uppercase"
      data-testid="tenant-footer"
    >
      {customText && (
        <div className="mb-1 normal-case tracking-normal text-[11px] text-[#a0aabf]">
          {customText}
        </div>
      )}
      {showPoweredBy && (
        <div data-testid="tenant-footer-powered-by">
          Powered by{" "}
          <span className="text-[var(--brand-primary,#1FB6C1)] font-semibold">
            Gemini Space
          </span>
        </div>
      )}
    </footer>
  );
}

